/**
 * 密码策略（见方案 5.1）。
 *
 * 设计取向：**长度优先 + 弱口令黑名单 + 与身份信息的相似度校验**，
 * 而不是"必须含大小写数字符号"的复杂度堆砌 —— OWASP 已不建议后者，
 * 因为它会促使用户写出 `Abc123!@#` 这类既难记又易猜的密码。
 */

/** 家长密码下限（长度）。 */
export const PARENT_MIN_PASSWORD_LENGTH = 12;
/** 学生密码下限：孩子输入成本考虑，允许更短，但必须避开弱口令。 */
export const STUDENT_MIN_PASSWORD_LENGTH = 8;

/**
 * 常见弱口令片段（小写匹配，只要包含即拒绝）。
 * 这不是完整的弱口令字典，而是"必然会被撞库"的高频项；
 * 生产环境建议再挂一份 HaveIBeenPwned 的 k-anonymity 范围查询。
 */
const WEAK_PATTERNS: string[] = [
  '123456',
  '12345678',
  '123456789',
  '1234567890',
  'qwerty',
  'qwertyuiop',
  'password',
  'passw0rd',
  'p@ssw0rd',
  'admin',
  'administrator',
  'root',
  'letmein',
  'welcome',
  'monkey',
  'dragon',
  'iloveyou',
  'abc123',
  '111111',
  '000000',
  '666666',
  '888888',
  '520520',
  'woaini',
  'woaini1314',
  '5201314',
  'a123456',
  'qwe123',
  'asd123',
  'zxcvbnm',
  '1qaz2wsx',
  'qazwsx',
  'starwars',
  'superman',
  'football',
  'baseball',
  'sunshine',
  'princess',
];

/** 全部字符相同 / 连续递增递减的简单序列检测。 */
function isSequentialOrRepeated(password: string): boolean {
  if (/^(.)\1+$/.test(password)) return true; // aaaaaa
  const codes = [...password].map((c) => c.charCodeAt(0));
  let asc = true;
  let desc = true;
  for (let i = 1; i < codes.length; i++) {
    if (codes[i]! - codes[i - 1]! !== 1) asc = false;
    if (codes[i]! - codes[i - 1]! !== -1) desc = false;
  }
  return asc || desc;
}

/** 键盘横向相邻序列（qwerty / asdfgh / zxcvbn 及其常见变体）。 */
function isKeyboardRun(password: string): boolean {
  const runs = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', 'qwertzuiop', 'poiuytrewq'];
  const lower = password.toLowerCase();
  return runs.some((run) => {
    for (let i = 0; i + 4 <= run.length; i++) {
      if (lower.includes(run.slice(i, i + 4))) return true;
    }
    return false;
  });
}

export interface PasswordContext {
  /** 用于相似度校验：用户名、邮箱、昵称等。 */
  identities?: string[];
  /** 最低长度，按角色传入。 */
  minLength?: number;
}

export interface PasswordCheckResult {
  ok: boolean;
  /** 给用户看的具体原因（中文）。 */
  reason?: string;
}

/**
 * 密码强度校验。**不做**大小写/符号的强制要求。
 */
export function checkPasswordStrength(
  password: string,
  context: PasswordContext = {},
): PasswordCheckResult {
  const minLength = context.minLength ?? PARENT_MIN_PASSWORD_LENGTH;

  if (typeof password !== 'string' || password.length < minLength) {
    return { ok: false, reason: `密码至少需要 ${minLength} 位` };
  }
  // 上限防止 DoS（Argon2 输入过长会浪费 CPU）
  if (password.length > 200) {
    return { ok: false, reason: '密码过长（最多 200 位）' };
  }

  const lower = password.toLowerCase();

  const hit = WEAK_PATTERNS.find((w) => lower.includes(w));
  if (hit) {
    return { ok: false, reason: '这个密码太常见了，容易被猜到，请换一个' };
  }

  if (isSequentialOrRepeated(password)) {
    return { ok: false, reason: '不要使用连续或重复的字符' };
  }

  if (isKeyboardRun(password)) {
    return { ok: false, reason: '不要使用键盘上的连续按键' };
  }

  // 与身份信息相似度：包含用户名/邮箱前缀即拒绝
  for (const identity of context.identities ?? []) {
    if (!identity) continue;
    const id = identity.toLowerCase().trim();
    if (id.length < 3) continue;
    if (lower.includes(id)) {
      return { ok: false, reason: '密码里不要包含你的用户名或邮箱' };
    }
    // 邮箱取 @ 前缀
    const local = id.split('@')[0];
    if (local && local.length >= 3 && lower.includes(local)) {
      return { ok: false, reason: '密码里不要包含你的用户名或邮箱' };
    }
  }

  return { ok: true };
}

/**
 * 粗略的强度评分（0..4），仅用于界面上的强度条提示。
 * 不是安全判断依据 —— 是否允许提交一律以 checkPasswordStrength 为准。
 */
export function passwordStrengthScore(password: string): 0 | 1 | 2 | 3 | 4 {
  if (!password) return 0;
  let score = 0;
  const variety =
    (/[a-z]/.test(password) ? 1 : 0) +
    (/[A-Z]/.test(password) ? 1 : 0) +
    (/\d/.test(password) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(password) ? 1 : 0);

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (variety >= 3) score++;
  if (variety === 4) score++;

  const penalty = checkPasswordStrength(password, { minLength: 1 }).ok ? 0 : 2;
  return Math.max(0, Math.min(4, score - penalty)) as 0 | 1 | 2 | 3 | 4;
}

/** 生成初始密码用的字母表（去掉易混淆字符 0/O、1/l/I）。 */
const INITIAL_PASSWORD_ALPHABET = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * 生成一个足够强、且便于家长念给孩子听的初始密码。
 *
 * ⚠️ 这个函数**必须保证**返回值能通过 `checkPasswordStrength`。原因：
 *   它是"自动生成初始密码"的唯一来源，生成后立刻会被拿去校验
 *   （`students.service.ts` 的 `assertStudentPasswordStrength`、
 *     `cli/operator.ts` 的 create-admin）。一旦生成出不合格的密码，
 *   调用方就会抛错 —— 表现为**创建学生/管理员偶发失败**，
 *   还带一句关于"键盘连续按键"的莫名提示，重试一次又好了。极难排查。
 *
 *   这个缺陷真实发生过：字母表只排除了 i/l/o（为降低孩子手抄出错率），
 *   但保留了 qwerty/asdfgh/zxcvbn 的全部字母，于是随机串里会以约
 *   0.033%（1/3000）的概率碰巧出现 `ASDF`/`SDFG`/`ZXCV` 这类键盘 4 连串，
 *   被 `isKeyboardRun` 拒绝。实测 20 万次生成有 66 次不合格。
 *
 * 两处修正：
 *  1. **拒绝采样**消除取模偏差（256 % 54 = 40，直接取模会让字母表前 40 个
 *     字符被选中的概率约为其他字符的 2 倍）。
 *  2. 生成后**用同一套强度检查验一遍**，不合格就重掷。
 *     12 位下失败率 0.033%，重掷几十次的累积失败概率可忽略。
 *
 * @param length 密码长度
 * @param randomBytes 仅用于测试注入确定性随机源；生产环境不要传。
 */
export function generateInitialPassword(
  length = 12,
  randomBytes?: (n: number) => Uint8Array,
): string {
  const alphabet = INITIAL_PASSWORD_ALPHABET;
  const fill =
    randomBytes ??
    ((n: number) => {
      const bytes = new Uint8Array(n);
      globalThis.crypto.getRandomValues(bytes);
      return bytes;
    });

  // 拒绝采样的上界：只接受 [0, max) 的字节，使每个字符等概率
  const max = Math.floor(256 / alphabet.length) * alphabet.length;

  const draw = (): string => {
    let out = '';
    // 每次多取一些字节，减少 getRandomValues 调用次数
    while (out.length < length) {
      for (const b of fill(length * 2)) {
        if (out.length >= length) break;
        if (b >= max) continue; // 落在偏差区间的字节直接丢弃
        out += alphabet[b % alphabet.length];
      }
    }
    return out;
  };

  const MIN_ATTEMPTS = 100;
  for (let attempt = 0; attempt < MIN_ATTEMPTS; attempt++) {
    const candidate = draw();
    // 用系统里最低的门槛（学生 8 位）校验：12 位产物必然同时满足家长门槛的长度要求
    if (checkPasswordStrength(candidate, { minLength: STUDENT_MIN_PASSWORD_LENGTH }).ok) {
      return candidate;
    }
  }

  // 数学上到不了这里（单次失败率 0.033%，100 次连续失败的概率约 1e-350）。
  // 保留显式报错而不是返回不合格密码 —— 宁可失败得明显，也不要让调用方拿到
  // 一个必然被自己拒绝的密码。
  throw new Error('无法生成满足强度要求的初始密码（这不应发生，请报告）');
}
